import React, { useCallback, useEffect, useState } from 'react';
import { AuthUserRow, Role } from './types';
import * as authApi from './authApi';
import { PasswordInput } from './PasswordInput';
import '../styles/common.css';
import './auth.css';

export function UserManagementPage({ readOnlyUsers = false }: { readOnlyUsers?: boolean }) {
  const [users, setUsers] = useState<AuthUserRow[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('USER');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setUsers(await authApi.listUsers());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setRole('USER');
    setError('');
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const closeAddDialog = () => {
    setShowAddDialog(false);
    resetForm();
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.createUser(username, password, role);
      closeAddDialog();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (name: string) => {
    if (!window.confirm(`Delete user "${name}"?`)) return;
    setError('');
    try {
      await authApi.deleteUser(name);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  return (
    <div className="configuration-panel">
      <div className="user-mgmt-header">
        <h2>User Management</h2>
        {!readOnlyUsers && (
          <button type="button" className="button primary" onClick={openAddDialog}>
            Add user
          </button>
        )}
      </div>
      {readOnlyUsers && (
        <p className="sync-readonly-hint">Secondary node — user create/delete disabled.</p>
      )}

      {error && !showAddDialog && <div className="auth-error">{error}</div>}

      <table className="user-mgmt-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Role</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.username}>
              <td>{u.username}</td>
              <td>{u.role}</td>
              <td>
                {readOnlyUsers ? (
                  <span className="user-mgmt-protected">—</span>
                ) : u.deletable !== false ? (
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => handleDelete(u.username)}
                  >
                    Delete
                  </button>
                ) : (
                  <span className="user-mgmt-protected">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showAddDialog && (
        <div className="popup-overlay" onClick={closeAddDialog}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2>Add user</h2>
              <button type="button" className="close-button" onClick={closeAddDialog}>
                ×
              </button>
            </div>
            <form onSubmit={handleAdd} autoComplete="off" data-form-type="other">
              <div className="popup-body">
                {error && <div className="auth-error">{error}</div>}
                <div className="form-group">
                  <label htmlFor="add-username">Username</label>
                  <input
                    id="add-username"
                    name="new-account-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-password">Password</label>
                  <PasswordInput
                    id="add-password"
                    name="new-account-password"
                    value={password}
                    onChange={setPassword}
                    autoComplete="off"
                    ignorePasswordManager
                    required
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-role">Role</label>
                  <select
                    id="add-role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as Role)}
                  >
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                  </select>
                </div>
              </div>
              <div className="popup-footer">
                <button
                  type="button"
                  className="button secondary"
                  onClick={closeAddDialog}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button type="submit" className="button primary" disabled={loading}>
                  {loading ? 'Adding…' : 'Add user'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
